import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, LayoutGrid,
  Clock, MapPin, User, Phone as PhoneIcon, Loader2, CheckCircle2, X, Sparkles,
} from 'lucide-react';
import { GlassCard, CardLabel, Pill, GradientAvatar } from './GlassCard';

/* ────────────────────────────────────────────────────────────────────────
   Glass-card Appointments / Booking page
   - Custom month + week + agenda views (no react-big-calendar)
   - "New appointment" inline modal
   ──────────────────────────────────────────────────────────────────────── */

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AppointmentsPage() {
  const { api } = useAuth();
  const [view, setView] = useState('month'); // month | week | agenda
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);

  const range = useMemo(() => computeRange(view, cursor), [view, cursor]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/calendar/events', {
        params: { range_start: range.start.toISOString(), range_end: range.end.toISOString() },
      });
      setEvents(data.events || []);
    } catch {
      // fallback: pull plain appointments only
      try {
        const { data } = await api.get('/appointments');
        setEvents((data.appointments || []).map(a => ({
          id: a.id, source: 'nextbell', title: a.title || 'Appointment',
          start: a.start_time, end: a.end_time, status: a.status || 'scheduled',
        })));
      } catch {}
    } finally { setLoading(false); }
  }, [api, range.start, range.end]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build labels for header
  const headerLabel = useMemo(() => {
    if (view === 'month') return `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === 'week') {
      const ws = startOfWeek(cursor);
      const we = addDays(ws, 6);
      const sameMonth = ws.getMonth() === we.getMonth();
      return sameMonth
        ? `${MONTH_NAMES[ws.getMonth()].slice(0,3)} ${ws.getDate()} to ${we.getDate()}, ${we.getFullYear()}`
        : `${MONTH_NAMES[ws.getMonth()].slice(0,3)} ${ws.getDate()} to ${MONTH_NAMES[we.getMonth()].slice(0,3)} ${we.getDate()}, ${we.getFullYear()}`;
    }
    return 'Upcoming';
  }, [view, cursor]);

  const move = (delta) => {
    if (view === 'month') {
      const d = new Date(cursor);
      d.setMonth(d.getMonth() + delta);
      setCursor(startOfMonth(d));
    } else if (view === 'week') {
      setCursor(addDays(startOfWeek(cursor), delta * 7));
    } else {
      setCursor(addDays(cursor, delta * 7));
    }
  };

  return (
    <div data-testid="appointments-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Bookings</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">{headerLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ViewToggle view={view} setView={setView} />

          <button data-testid="add-appointment-btn" onClick={() => setShowNewModal(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full px-4 py-2 inline-flex items-center gap-1.5 shadow-lg shadow-blue-500/20">
            <Plus className="w-4 h-4" /> New
          </button>
        </div>
      </div>

      {/* Sub-header: nav + KPI */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button onClick={() => move(-1)} aria-label="Previous"
            className="w-9 h-9 rounded-full border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/15 flex items-center justify-center text-gray-700 dark:text-slate-300">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setCursor(view === 'month' ? startOfMonth(new Date()) : new Date())}
            className="px-4 h-9 rounded-full border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/15 text-sm text-gray-700 dark:text-slate-300">
            Today
          </button>
          <button onClick={() => move(1)} aria-label="Next"
            className="w-9 h-9 rounded-full border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/15 flex items-center justify-center text-gray-700 dark:text-slate-300">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
          <LegendDot color="bg-blue-500" /> Nextbell
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <GlassCard className="p-16 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-slate-500" />
        </GlassCard>
      ) : view === 'month' ? (
        <MonthView cursor={cursor} events={events} onSelectDay={(d) => {
          setCursor(d); setView('week');
        }} onSelectEvent={setActiveEvent} />
      ) : view === 'week' ? (
        <WeekView cursor={cursor} events={events} onSelectEvent={setActiveEvent} />
      ) : (
        <AgendaView events={events} onSelectEvent={setActiveEvent} />
      )}

      {showNewModal && (
        <NewAppointmentModal
          api={api}
          onClose={() => setShowNewModal(false)}
          onCreated={() => { setShowNewModal(false); refresh(); }}
          initialStart={new Date()}
        />
      )}

      {activeEvent && (
        <EventDetailModal event={activeEvent} onClose={() => setActiveEvent(null)} />
      )}
    </div>
  );
}

/* ───── View toggle ───── */
function ViewToggle({ view, setView }) {
  const opts = [
    { key: 'month', icon: LayoutGrid, label: 'Month' },
    { key: 'week', icon: CalendarIcon, label: 'Week' },
    { key: 'agenda', icon: List, label: 'List' },
  ];
  return (
    <div className="flex bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-full p-0.5">
      {opts.map(o => {
        const active = view === o.key;
        const Icon = o.icon;
        return (
          <button key={o.key} onClick={() => setView(o.key)}
            data-testid={`view-${o.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              active
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'}`}>
            <Icon className="w-3.5 h-3.5" /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ───── Month view ───── */
function MonthView({ cursor, events, onSelectDay, onSelectEvent }) {
  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const today = new Date();
  const eventsByDay = useMemo(() => groupByDay(events), [events]);

  return (
    <GlassCard className="p-4 md:p-5">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500 font-medium text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map(d => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const list = eventsByDay[ymd(d)] || [];
          return (
            <button key={d.toISOString()} onClick={() => onSelectDay(d)}
              className={`group relative text-left rounded-xl border p-2 min-h-[88px] md:min-h-[110px] flex flex-col gap-1 transition-colors
                ${inMonth
                  ? 'bg-white dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] hover:border-blue-300 dark:hover:border-blue-500/40'
                  : 'bg-gray-50/40 dark:bg-white/[0.01] border-transparent text-gray-400'}
                ${isToday ? 'ring-2 ring-blue-500/40 dark:ring-blue-500/50' : ''}`}>
              <span className={`text-xs font-medium ${
                isToday ? 'text-blue-600 dark:text-blue-400' :
                inMonth ? 'text-gray-700 dark:text-slate-300' : 'text-gray-400 dark:text-slate-600'}`}>{d.getDate()}</span>
              <div className="flex-1 space-y-0.5 overflow-hidden">
                {list.slice(0, 3).map(ev => (
                  <span key={ev.id} onClick={(e) => { e.stopPropagation(); onSelectEvent(ev); }}
                    className={`block w-full truncate text-[10px] px-1.5 py-0.5 rounded font-medium cursor-pointer ${eventChipColor(ev)}`}>
                    {fmtTime(ev.start)} {ev.title}
                  </span>
                ))}
                {list.length > 3 && (
                  <span className="block text-[10px] text-gray-500 dark:text-slate-400 px-1.5">+{list.length - 3} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </GlassCard>
  );
}

/* ───── Week view ───── */
function WeekView({ cursor, events, onSelectEvent }) {
  const ws = startOfWeek(cursor);
  const days = Array.from({length: 7}, (_, i) => addDays(ws, i));
  const today = new Date();
  const hours = Array.from({length: 14}, (_, i) => i + 7); // 7am – 8pm
  const eventsByDay = useMemo(() => groupByDay(events), [events]);

  return (
    <GlassCard className="p-0 overflow-hidden">
      <div className="grid grid-cols-[60px_1fr] border-b border-gray-100 dark:border-white/[0.06]">
        <div />
        <div className="grid grid-cols-7">
          {days.map(d => {
            const isToday = sameDay(d, today);
            return (
              <div key={d.toISOString()}
                className={`py-3 text-center border-l border-gray-100 dark:border-white/[0.06] ${isToday ? 'bg-blue-50/50 dark:bg-blue-500/[0.07]' : ''}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500 font-medium">
                  {DAY_NAMES[d.getDay()]}
                </div>
                <div className={`text-lg font-semibold ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-[60px_1fr] max-h-[640px] overflow-y-auto">
        <div className="flex flex-col">
          {hours.map(h => (
            <div key={h} className="h-16 border-b border-gray-100 dark:border-white/[0.04] pr-2 pt-1 text-right">
              <span className="text-[10px] text-gray-400 dark:text-slate-500">{fmtHour(h)}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 relative">
          {days.map(d => {
            const list = (eventsByDay[ymd(d)] || []).filter(ev => !ev.all_day);
            return (
              <div key={d.toISOString()} className="relative border-l border-gray-100 dark:border-white/[0.06]">
                {hours.map(h => (
                  <div key={h} className="h-16 border-b border-gray-100 dark:border-white/[0.04]" />
                ))}
                {list.map(ev => {
                  const start = new Date(ev.start);
                  const end = ev.end ? new Date(ev.end) : new Date(start.getTime() + 60 * 60 * 1000);
                  const startHrs = start.getHours() + start.getMinutes() / 60;
                  const endHrs = end.getHours() + end.getMinutes() / 60;
                  const top = (startHrs - 7) * 64;
                  const height = Math.max(28, (endHrs - startHrs) * 64);
                  if (top < -64 || top > 14 * 64) return null;
                  return (
                    <div key={ev.id} onClick={() => onSelectEvent(ev)}
                      className={`absolute left-1 right-1 rounded-lg px-2 py-1 text-[11px] font-medium shadow-sm cursor-pointer overflow-hidden ${eventChipColor(ev)}`}
                      style={{ top: Math.max(0, top), height }}>
                      <p className="truncate">{ev.title}</p>
                      <p className="opacity-80 text-[10px] truncate">{fmtTime(ev.start)}{ev.end ? ` to ${fmtTime(ev.end)}` : ''}</p>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </GlassCard>
  );
}

/* ───── Agenda / list view ───── */
function AgendaView({ events, onSelectEvent }) {
  const upcoming = events
    .filter(e => e.start)
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  if (upcoming.length === 0) {
    return (
      <GlassCard className="p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.05] mx-auto mb-3 flex items-center justify-center">
          <CalendarIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400">No bookings yet. Click <span className="font-medium text-gray-900 dark:text-white">+ New</span> to add one.</p>
      </GlassCard>
    );
  }

  // Group by day
  const groups = {};
  upcoming.forEach(ev => {
    const k = ymd(new Date(ev.start));
    (groups[k] = groups[k] || []).push(ev);
  });

  return (
    <div className="space-y-3">
      {Object.entries(groups).map(([k, list]) => {
        const d = new Date(k + 'T00:00:00');
        const todayIs = sameDay(d, new Date());
        return (
          <GlassCard key={k} className="p-5">
            <div className="flex items-baseline gap-3 mb-3">
              <span className={`text-lg font-semibold ${todayIs ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                {todayIs ? 'Today' : DAY_NAMES[d.getDay()]}
              </span>
              <CardLabel>{MONTH_NAMES[d.getMonth()].slice(0,3)} {d.getDate()}</CardLabel>
              <span className="ml-auto text-xs text-gray-400 dark:text-slate-500">{list.length} event{list.length>1?'s':''}</span>
            </div>
            <div className="space-y-2">
              {list.map(ev => (
                <button key={ev.id} onClick={() => onSelectEvent(ev)}
                  data-testid={`appt-row-${ev.id}`}
                  className="w-full flex items-start gap-3 px-3 py-3 rounded-xl border border-gray-100 dark:border-white/[0.05] hover:border-blue-300 dark:hover:border-blue-500/40 hover:bg-gray-50 dark:hover:bg-white/[0.04] text-left transition-colors">
                  <div className="w-1 self-stretch rounded-full bg-blue-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ev.title}</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {fmtTime(ev.start)}{ev.end ? ` to ${fmtTime(ev.end)}` : ''}
                      {ev.location && <><MapPin className="w-3 h-3 ml-2" /> <span className="truncate">{ev.location}</span></>}
                    </p>
                    {ev.attendee && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 truncate">{ev.attendee}</p>}
                  </div>
                  <Pill tone={statusTone(ev.status)} className="ml-1 hidden sm:inline-flex">{ev.status || 'scheduled'}</Pill>
                </button>
              ))}
            </div>
          </GlassCard>
        );
      })}
    </div>
  );
}

/* ───── New appointment modal ───── */
function NewAppointmentModal({ api, onClose, onCreated, initialStart }) {
  const [form, setForm] = useState({
    title: '',
    start_time: toLocalInput(initialStart),
    duration_minutes: 60,
    notes: '',
    location: '',
    attendee: '',
    phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const start = new Date(form.start_time);
      const end = new Date(start.getTime() + form.duration_minutes * 60 * 1000);
      await api.post('/appointments', {
        title: form.title,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        duration_minutes: form.duration_minutes,
        notes: form.notes,
        location: form.location,
      });
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save appointment.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <GlassCard className="w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-heading text-xl font-semibold text-gray-900 dark:text-white">New booking</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">Add a manual appointment to your calendar.</p>

        {err && <div className="bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-700 dark:text-red-300 mb-3">{err}</div>}

        <div className="space-y-3">
          <ModalField label="Title">
            <input data-testid="appt-title-input" value={form.title} onChange={e => setForm(s => ({...s, title: e.target.value}))}
              placeholder="Appointment title (e.g. Consultation, John Smith)"
              className="w-full bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500" />
          </ModalField>
          <div className="grid grid-cols-2 gap-2">
            <ModalField label="Start">
              <input data-testid="appt-start-input" type="datetime-local" value={form.start_time}
                onChange={e => setForm(s => ({...s, start_time: e.target.value}))}
                className="w-full bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" />
            </ModalField>
            <ModalField label="Duration">
              <select value={form.duration_minutes} onChange={e => setForm(s => ({...s, duration_minutes: Number(e.target.value)}))}
                className="w-full bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500">
                <option value={15}>15 min</option><option value={30}>30 min</option>
                <option value={45}>45 min</option><option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option><option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
              </select>
            </ModalField>
          </div>
          <ModalField label="Location (optional)">
            <input value={form.location} onChange={e => setForm(s => ({...s, location: e.target.value}))}
              placeholder="123 Main St / Zoom link"
              className="w-full bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500" />
          </ModalField>
          <ModalField label="Notes (optional)">
            <textarea value={form.notes} onChange={e => setForm(s => ({...s, notes: e.target.value}))}
              placeholder="Anything Ava should remember"
              className="w-full bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-blue-500 h-20 resize-none" />
          </ModalField>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-100 dark:bg-white/[0.05] text-gray-700 dark:text-slate-200 rounded-full py-2.5 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/[0.08]">Cancel</button>
          <button data-testid="appt-save-btn" onClick={submit} disabled={busy || !form.title || !form.start_time}
            className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-full py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving...</> : <><CheckCircle2 className="w-4 h-4"/> Save</>}
          </button>
        </div>
      </GlassCard>
    </div>
  );
}

/* ───── Event detail ───── */
function EventDetailModal({ event, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <GlassCard className="w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-start gap-3 mb-4">
          {event.attendee
            ? <GradientAvatar name={event.attendee} size={44} />
            : (
              <div className="w-11 h-11 rounded-full flex items-center justify-center bg-blue-500/15 text-blue-500">
                <Sparkles className="w-5 h-5" />
              </div>
            )}
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-xl font-semibold text-gray-900 dark:text-white truncate">{event.title}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Pill tone="purple"><Sparkles className="w-2.5 h-2.5" /> Nextbell</Pill>
              <Pill tone={statusTone(event.status)}>{event.status || 'scheduled'}</Pill>
            </div>
          </div>
        </div>

        <div className="space-y-2.5 text-sm">
          <DetailRow icon={Clock} label="When" value={`${fmtFull(event.start)}${event.end ? ' to ' + fmtTime(event.end) : ''}`} />
          {event.location && <DetailRow icon={MapPin} label="Where" value={event.location} />}
          {event.attendee && <DetailRow icon={User} label="With" value={event.attendee} />}
          {event.phone && <DetailRow icon={PhoneIcon} label="Phone" value={event.phone} />}
        </div>
      </GlassCard>
    </div>
  );
}

/* ───── Bits ───── */
function ModalField({ label, children }) {
  return (
    <label className="block">
      <CardLabel>{label}</CardLabel>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <CardLabel>{label}</CardLabel>
        <p className="text-sm text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}
function LegendDot({ color }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${color} mr-1`} />;
}

/* ───── helpers ───── */
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d) { return d.toISOString().slice(0, 10); }
function sameDay(a, b) { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
function fmtFull(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' +
      d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
function fmtHour(h) {
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh} ${ap}`;
}
function toLocalInput(d) {
  const dd = new Date(d);
  const tzOffset = dd.getTimezoneOffset() * 60000;
  return new Date(dd.getTime() - tzOffset).toISOString().slice(0, 16);
}
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  const start = startOfWeek(first);
  return Array.from({length: 42}, (_, i) => addDays(start, i));
}
function computeRange(view, cursor) {
  if (view === 'month') {
    const first = startOfMonth(cursor);
    return { start: addDays(first, -7), end: addDays(first, 42) };
  }
  if (view === 'week') {
    const ws = startOfWeek(cursor);
    return { start: ws, end: addDays(ws, 7) };
  }
  return { start: addDays(new Date(), -1), end: addDays(new Date(), 60) };
}
function groupByDay(events) {
  const out = {};
  for (const ev of events) {
    if (!ev.start) continue;
    const k = ymd(new Date(ev.start));
    (out[k] = out[k] || []).push(ev);
  }
  return out;
}
function eventChipColor(ev) {
  switch ((ev.status || '').toLowerCase()) {
    case 'confirmed': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300';
    case 'completed': return 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300';
    default: return 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300';
  }
}
function statusTone(s) {
  switch ((s || '').toLowerCase()) {
    case 'confirmed': return 'hot';
    case 'cancelled': return 'red';
    case 'completed': return 'purple';
    default: return 'blue';
  }
}
